import type { OperationsSummary } from "@/lib/types";

// 三格互斥漏斗：全集顯示在摘要句，三格各對應一個階段，點擊即篩選（再點取消）。
export type HomeQueueFilter = "all" | "pending" | "preparing" | "ready_to_ship";

export interface HomeOverviewModel {
  pendingCount: number;
  preparingCount: number;
  readyCount: number;
  activeTotal: number;
  summaryText: string;
}

function formatFullDate(dateKey: string) {
  return new Date(`${dateKey}T00:00:00`).toLocaleDateString("zh-TW", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

function formatShortDate(dateKey: string) {
  return new Date(`${dateKey}T00:00:00`).toLocaleDateString("zh-TW", {
    month: "numeric",
    day: "numeric",
  });
}

/**
 * G-12：三格漏斗與摘要句一律取自後端 SQL 聚合（不受清單 LIMIT 500 影響）。
 * summary 尚未載入（或非 admin/staff）時退回 0，摘要句顯示「目前沒有待辦訂單」。
 */
export function buildHomeOverviewModel(
  summary: OperationsSummary | undefined,
): HomeOverviewModel {
  const pendingCount = summary?.queue.pending ?? 0;
  const preparingCount = summary?.queue.preparing ?? 0;
  const readyCount = summary?.queue.readyToShip ?? 0;
  const activeTotal = pendingCount + preparingCount + readyCount;
  const nextShipment = summary?.nextShipment ?? null;
  const overdue = summary?.overdue ?? null;

  // 規格：一句合併摘要。J-04a：逾期與下一出貨分開，逾期可能跨年所以帶年份。
  const parts = activeTotal > 0 ? [`今天 ${activeTotal} 筆待辦`] : [];
  if (overdue) {
    parts.push(`逾期 ${overdue.orderCount} 筆（最早 ${formatFullDate(overdue.earliestDate)}）`);
  }
  if (nextShipment) {
    parts.push(
      `下一出貨 ${formatShortDate(nextShipment.date)}（${nextShipment.orderCount} 筆 / ${nextShipment.totalKilograms.toFixed(0)} kg）`,
    );
  }
  // 全形括號結尾後面不再補空格：「（…）· 下一出貨」
  const summaryText =
    parts.length > 0
      ? parts.reduce((text, part) => `${text}${text.endsWith("）") ? "· " : " · "}${part}`)
      : "目前沒有待辦訂單";

  return { pendingCount, preparingCount, readyCount, activeTotal, summaryText };
}
